// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import update from 'immutability-helper';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import HowToOrderModal from './HowToOrderModal/HowToOrderModal';
import Filter from '../../../elements/Filter/Filter';
import FilterDrawer from '../../../elements/Filter/Drawer/FilterDrawer';
import { Mobile, Default } from '../../../utils/responsive';
import { generateTags } from '../../../elements/Filter/utils';
import InfiniteScroll from '../../../elements/InfiniteScroll/InfiniteScroll';
import Loading from '../../../elements/Loading/Loading';

import style from './Stores.scss';
import filterOptionsStyle from '../../../assets/styles/filter-options.scss';
import icons from '../../../assets/styles/icons.scss';
import filterStyles from '../../../assets/styles/filter.scss';
import storeImg from '../../../assets/img/stores/steps/stores1.png';
import storeImgMobile from '../../../assets/img/stores/steps/stores2.png';

import type { OnlineStore, OnlineStoreCategory } from '../../../entities';
import type { FilterCheckbox } from '../../../elements/Filter/Filter';
import type { StoresFilter } from '../duck';
import type { LocaleState } from '../../locale/duck';
import type { LocalValuesState } from '../../local-values/duck';
import AlertContainer from '../../alert/container/AlertContainer';

type Props = {
  +localValues: LocalValuesState;
  +locale: LocaleState;
  +stores: OnlineStore[];
  +fetchStores: (filters?: StoresFilter, resetPagination?: boolean) => void;
  +goToRedirect: (route: string) => void;
  +isFetchingStores?: boolean;
  +resetState: () => void;
  +pageCanAsk: boolean;
};

type State = {
  openModal: boolean;
  drawerOpen: boolean;
  filter: {
    name: {
      value: string;
    };
    openName: boolean;
    categories: {
      [key: string]: boolean;
    };
    openCategories: boolean;
  };
  storeSelected: OnlineStore;
  sort: {
    value: string;
    name: string;
  };
  order: {
    value: 'ASC' | 'DESC';
  };
};

const initialState = {
  openModal: false,
  drawerOpen: false,
  filter: {
    name: {
      value: '',
    },
    openName: true,
    categories: {
      // popular are default checked
      '1': true,
    },
    openCategories: true,
  },
  storeSelected: {},
  sort: {
    value: '',
    name: '',
  },
  order: {
    value: 'ASC',
  },
};

/*const countriesImages = {
  US: coverUSImg,
  PE: coverPEImg,
  CO: coverCOImg,
};*/

const messages = defineMessages({
  PE: {
    metaTitle: {
      id: 'stores.document.title.PE',
    },
    metaDescription: {
      id: 'stores.document.description.PE',
    },
    subtitle: {
      id: 'stores.header.subtitle.PE',
    },
  },
  CO: {
    metaTitle: {
      id: 'stores.document.title.CO',
    },
    metaDescription: {
      id: 'stores.document.description.CO',
    },
    subtitle: {
      id: 'stores.header.subtitle.CO',
    },
  },
  US: {
    metaTitle: {
      id: 'stores.document.title.US',
    },
    metaDescription: {
      id: 'stores.document.description.US',
    },
    subtitle: {
      id: 'stores.header.subtitle.US',
    },
  },
  default: {
    metaTitle: {
      id: 'stores.document.title.default',
    },
    metaDescription: {
      id: 'stores.document.description.default',
    },
    subtitle: {
      id: 'stores.header.subtitle.default',
    },
  },
  title: {
    id: 'stores.header.title'
  },
  sortAscName: {
    id: 'sort.option.ascName',
  },
  sortDescName: {
    id: 'sort.option.descName',
  },
});

export default class Stores extends Component<Props, State> {
  state = initialState;

  componentDidMount() {
    const { fetchStores, stores } = this.props;
    if (!stores) {
      // fetchStores({ categories: [] }, true);
      fetchStores(this.buildQueryParams(), true);
    }
  }

  componentWillUnmount() {
    const { resetState } = this.props;
    resetState();
  }

  handleOpenDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  handleCloseDrawer = () => {
    const { fetchStores } = this.props;
    this.setState({
      drawerOpen: false,
    });
    fetchStores(this.buildQueryParams(), true);
  };

  handleOpen = (name: string) => {
    this.setState((state: State) => {
      return {
        filter: update(state.filter, {
          [name]: { $set: !state.filter[name] }
        }),
      };
    });
  };

  handleChangeName(e: Event, isDesktop: boolean = true) {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('name', { value });
    }
  }

  handleChangeCategory(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('categories', { [value]: !filter.categories[value] });
    }
  }

  buildQueryParams(): { [key: string]: any } {
    const params = {};
    const { filter, sort, order } = this.state;
    const categories = Object.keys(filter.categories)
      .reduce((acum, act: string) => filter.categories[act] ? acum.concat(act) : acum, []);
    if (filter.name.value) params.name = filter.name.value.toLowerCase();
    if (categories.length) params.categories = categories;
    // Sort & Ord
    if (sort.name) params.sort = sort.name;
    if (order.value) params.order = order.value;
    return params;
  }

  handleClearFilters = () => {
    const { fetchStores } = this.props;
    this.setState(initialState,
      () => fetchStores(this.buildQueryParams(), true));
  };

  handleDeleteTag = (key?: string, name?: string) => {
    const { fetchStores } = this.props;
    const updateQuery = {};
    switch (key) {
      case 'categories':
      case 'years':
        if (name) {
          updateQuery[key] = {
            [name]: { $set: false }
          };
        }
        break;
      case 'name':
        updateQuery[key] = {
          value: { $set: '' }
        };
        break;
    }
    this.setState((prevState: State) => update(prevState, {
      filter: updateQuery,
    }), () => fetchStores(this.buildQueryParams(), true));
  };

  // Depends if desktop or mobile
  handleChangeFilterValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      const { fetchStores } = this.props;
      this.setState((state: State) => {
        const c = update(state.filter[name], { $merge: value });
        return {
          filter: update(state.filter, {
            [name]: { $set: c }
          }),
        };
      }, () => {
        if (isDesktop) {
          fetchStores(this.buildQueryParams(), true);
        }
      });
    };

  generateFilterFields = (categories: ?FilterCheckbox[], { filter }: State, isDesktop: boolean) => [
    {
      type: 'text',
      // titleIntl: 'filter.name.title',
      placeholderIntl: 'stores.filter.name.title',
      handler: (e: Event) => this.handleChangeName(e, isDesktop),
      stateValue: filter.name,
      name: 'name',
      icon: icons.symbolSearch,
      isOpen: filter.openName,
      handleOpen: () => this.handleOpen('openName'),
    },
    {
      type: 'checkbox',
      titleIntl: 'filter.categories.title',
      handler: (e: Event) => this.handleChangeCategory(e, isDesktop),
      stateValue: filter.categories,
      name: 'categories',
      checkboxes: categories,
      isOpen: filter.openCategories,
      handleOpen: () => this.handleOpen('openCategories'),
    },
  ];

  /* open and close store detail modal */
  showModal(store: OnlineStore) {
    this.setState({
      openModal: true,
      storeSelected: store,
    });
  }

  handleCloseModal = () => {
    this.setState({
      openModal: false,
      storeSelected: {},
    });
  };

  handleChangeSort = (e: Event) => {
    const { fetchStores } = this.props;
    if (e.target instanceof HTMLSelectElement) {
      const option = e.target.options[e.target.selectedIndex];
      const dataset: { sort: string, order: 'ASC' | 'DESC' } = (option.dataset: any);
      this.setState({
        sort: {
          value: option.value,
          name: dataset.sort,
        },
        order: {
          value: dataset.order,
        },
      }, () => fetchStores(this.buildQueryParams(), true));
    }
  };

  render() {
    const { drawerOpen, openModal, storeSelected, sort, filter } = this.state;
    const { stores, goToRedirect, locale, localValues, fetchStores, isFetchingStores, pageCanAsk } = this.props;

    const description = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).metaDescription);
    const title = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).metaTitle);
    // TODO: CHANGE EMPTY ARRAY
    const upperLocale = locale.locale ? locale.locale.toUpperCase() : 'EN';
    const onlineStoresCategories: FilterCheckbox[] = !localValues.storeCategories ? [] :
      localValues.storeCategories.map((storeCategory: OnlineStoreCategory) => ({
        label: storeCategory.translations[upperLocale].name,
        value: `${storeCategory.id}`,
      }));
    const filterFields = this.generateFilterFields(onlineStoresCategories, this.state, true);
    const filterFieldsMobile = this.generateFilterFields(onlineStoresCategories, this.state, false);
    const filterContent = <Filter filterFields={(filterFields: any)}
                                  handleDeleteTag={this.handleDeleteTag}/>;

    const storesBox = stores && stores.map((store: OnlineStore, index: number) => {
      return (
        <Link className={style.storeCards}
              key={index}
              to={`/store/${store.slug}`}>
          {store.logo ? <picture>
            <img src={store.logo}
                 alt={store.name}/>
          </picture> : <span>{store.name}</span>}
        </Link>
      );
    });

    const tags: any = generateTags((filterFields: any), this.handleDeleteTag, this.context.intl.formatMessage);
    const sortNativeSelect = (
      <select value={sort.value}
              onChange={this.handleChangeSort}>
        <option value="ascName" data-sort="NAME"
                data-order="ASC">{this.context.intl.formatMessage(messages.sortAscName)}</option>
        <option value="descName" data-sort="NAME"
                data-order="DESC">{this.context.intl.formatMessage(messages.sortDescName)}</option>
      </select>
    );
    return (
      <Fragment>
        <Helmet meta={[
          { name: 'description', content: description },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { name: 'twitter:title', content: title },
          { name: 'twitter:twitter:description', content: description },
        ]}>
          <title>{title}</title>
        </Helmet>
        <header className={style.storeHeader}>
          <section className={style.headerWrap}>
            <section className={style.titles}>
              <h1 className={style.desktop}>{this.context.intl.formatMessage(messages.title)}</h1>
              <p>{this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).subtitle)}</p>
            </section>
            <Mobile>
              <img src={storeImgMobile}
                   alt="Qempo"/>
            </Mobile>
            <Default>
              <img src={storeImg}
                   alt="Qempo"/>
            </Default>
          </section>
        </header>
        <section className={filterOptionsStyle.filterOptions}>
          <section className={filterOptionsStyle.header}>
            <FormattedMessage id="stores.filter.title">{
              text => <p className={filterOptionsStyle.title}><i className={icons.symbolTweak}/>{text}</p>
            }</FormattedMessage>
            <section className={filterOptionsStyle.sortOptions}>
              <FormattedMessage id="sort.title">{
                txt => <label>{txt}</label>
              }</FormattedMessage>
              {sortNativeSelect}
              {/*<i className={classNames(filterOptionsStyle.down, icons.iconChevronDownMedium)}/>
              <i className={classNames(filterOptionsStyle.up, icons.iconChevronUpMedium)}/>*/}
            </section>
            <section className={filterOptionsStyle.filterDrawer}>
              <FilterDrawer drawerOpen={drawerOpen}
                            filterFields={(filterFieldsMobile: any)}
                            color="lightBlue"
                            handleCloseDrawer={this.handleCloseDrawer}
                            handleOpenDrawer={this.handleOpenDrawer}
                            handleClearFilters={this.handleClearFilters}/>
            </section>
          </section>
        </section>

        <section key="mainWrap" className={style.mainWrap}>
          <aside>
            {filterContent}
          </aside>
          <section className={style.rightWrap}>
            {!!tags.length && <ul className={filterStyles.tags}>
              {tags}
            </ul>}
            <InfiniteScroll offset={650}
                             disabled={!!isFetchingStores || pageCanAsk === false}
                             handleCheckpoint={() => fetchStores(this.buildQueryParams())}>
              <section className={style.storesWrap}>
                {storesBox}
              </section>
            </InfiniteScroll>
            {isFetchingStores && <Loading className={style.loading}/>}
            {!isFetchingStores && stores && stores.length === 0 &&
            <FormattedMessage id="stores.noResults">{
              txt => <p className={style.noResults}>{txt}</p>
            }</FormattedMessage>}
          </section>
        </section>
        <HowToOrderModal open={openModal}
                         store={storeSelected}
                         goToRedirect={goToRedirect}
                         handleClose={this.handleCloseModal}/>
      </Fragment>
    );
  }
}

Stores.propTypes = {
  localValues: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  stores: PropTypes.array,
  fetchStores: PropTypes.func.isRequired,
  goToRedirect: PropTypes.func.isRequired,
  isFetchingStores: PropTypes.bool,
  resetState: PropTypes.func.isRequired,
  pageCanAsk: PropTypes.bool,
};

Stores.contextTypes = {
  intl: intlShape,
};




// WEBPACK FOOTER //
// ./src/modules/stores/components/Stores.jsx